// Tiny browser-storage prefs so the chat surface survives a page refresh.
// Each key stays narrow:
//   - whether the chat panel is open
//   - which panel mode (sidebar / floating / fullscreen) is selected
//   - which general conversation this browser SESSION is on
//   - a one-shot "open this conversation" intent (used across a new tab)
// Callout-mode conversation selection is driven by the route URL, so we
// only track conversation ids for the general agent.
//
// The general chat is session-scoped on purpose: a conversation pins its agent
// version, prompt and skills on its first run, so reopening the same chat
// forever keeps a user on a stale agent. A new browser session starts a new
// chat; a reload within the session resumes where it left off.

const CHAT_VISIBLE_KEY = 'phoebe-chat-visible';
const CHAT_EXPANDED_KEY_LEGACY = 'phoebe-chat-expanded';
const CHAT_MODE_KEY = 'phoebe-chat-mode';
// Mirrors the session value; read only when sessionStorage is unavailable
// (private mode), so a reload there still restores instead of losing the chat.
const GENERAL_LAST_CONVERSATION_KEY = 'phoebe-chat-last-general-conversation';
const GENERAL_SESSION_CHAT_KEY = 'phoebe-chat-general-session';
const GENERAL_OPEN_INTENT_KEY = 'phoebe-chat-general-open-intent';
// The session is on a chat the user hasn't sent to yet (no conversation row).
const NEW_CHAT_SESSION_VALUE = 'new';
// An open intent is claimed by the tab it opens, so it is stale within seconds.
const OPEN_INTENT_TTL_MS = 60_000;

export type ChatPanelMode = 'sidebar' | 'floating' | 'fullscreen';

function isChatPanelMode(value: string | null): value is ChatPanelMode {
    return (
        value === 'sidebar' || value === 'floating' || value === 'fullscreen'
    );
}

function safeReadString(key: string): string | null {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function safeWriteString(key: string, value: string | null): void {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        if (value === null) {
            window.localStorage.removeItem(key);
            return;
        }
        window.localStorage.setItem(key, value);
    } catch {
        // localStorage unavailable (private browsing, quota exceeded)
    }
}

type GeneralConversationPersistenceScope = {
    readonly organizationId: string | null;
    readonly userId: string | null;
};

function getGeneralLastConversationKey({
    organizationId,
    userId,
}: GeneralConversationPersistenceScope): string | null {
    return organizationId && userId
        ? `${GENERAL_LAST_CONVERSATION_KEY}:${userId}:${organizationId}`
        : null;
}

export function readPersistedChatVisible(): boolean {
    return safeReadString(CHAT_VISIBLE_KEY) === 'true';
}

export function writePersistedChatVisible(visible: boolean): void {
    safeWriteString(CHAT_VISIBLE_KEY, visible ? 'true' : 'false');
}

export function readPersistedChatMode(): ChatPanelMode {
    const raw = safeReadString(CHAT_MODE_KEY);
    if (isChatPanelMode(raw)) {
        return raw;
    }
    // Migrate the legacy boolean if no tri-state value is stored yet, then
    // drop the legacy key so it doesn't pollute storage on subsequent reads.
    if (safeReadString(CHAT_EXPANDED_KEY_LEGACY) === 'true') {
        safeWriteString(CHAT_EXPANDED_KEY_LEGACY, null);
        return 'fullscreen';
    }
    return 'sidebar';
}

export function writePersistedChatMode(mode: ChatPanelMode): void {
    safeWriteString(CHAT_MODE_KEY, mode);
}

function getGeneralSessionChatKey({
    organizationId,
    userId,
}: GeneralConversationPersistenceScope): string | null {
    return organizationId && userId
        ? `${GENERAL_SESSION_CHAT_KEY}:${userId}:${organizationId}`
        : null;
}

function getGeneralOpenIntentKey({
    organizationId,
    userId,
}: GeneralConversationPersistenceScope): string | null {
    return organizationId && userId
        ? `${GENERAL_OPEN_INTENT_KEY}:${userId}:${organizationId}`
        : null;
}

export type GeneralChatSelection = {
    /** The conversation to open; null means start a new chat. */
    readonly conversationId: string | null;
    /**
     * The session is on a new, not-yet-created chat, so the caller must keep
     * the draft rather than falling back to the most recent conversation.
     */
    readonly isNewChat: boolean;
};

/**
 * Which general conversation this browser session is on.
 *
 * A new browser session (no session value) starts a new chat, so the user
 * picks up the current agent version instead of resuming a chat pinned to an
 * old one. An explicit open intent — a notification opening the chat in a new
 * tab — still wins, because the user asked for that conversation by name.
 *
 * When sessionStorage is unavailable (private mode) we cannot tell sessions
 * apart, so we fall back to the mirrored last conversation: resuming is a safer
 * degrade than silently dropping the user's chat on every reload.
 */
export function readGeneralChatSelection(
    scope: GeneralConversationPersistenceScope,
): GeneralChatSelection {
    const sessionKey = getGeneralSessionChatKey(scope);
    if (!sessionKey || typeof window === 'undefined') {
        return { conversationId: null, isNewChat: false };
    }

    let sessionValue: string | null;
    try {
        sessionValue = window.sessionStorage.getItem(sessionKey);
    } catch {
        const mirrored = readMirroredGeneralConversationId(scope);
        return { conversationId: mirrored, isNewChat: false };
    }

    // A tab that already owns a conversation keeps it. An intent must never
    // pull a tab off the chat it is on — every tab that opens the chat is given
    // a fresh session (`noopener`, see openInBackgroundTab), so a tab acting on
    // an intent has no session of its own yet.
    if (sessionValue !== null && sessionValue !== NEW_CHAT_SESSION_VALUE) {
        return { conversationId: sessionValue, isNewChat: false };
    }

    const intent = readGeneralChatOpenIntent(scope);
    if (intent) {
        return { conversationId: intent, isNewChat: false };
    }

    // Fresh session, or a session still drafting its new chat.
    return { conversationId: null, isNewChat: true };
}

/**
 * The pending open intent, if it is still fresh.
 *
 * The intent is written immediately before the new tab opens, so it is claimed
 * within seconds. It expires rather than living in localStorage forever: a tab
 * that never opens (blocked popup, closed tab, failed load) leaves the intent
 * unclaimed, and a permanent one would reopen that conversation for every later
 * session — quietly turning the new-chat-per-session behavior off for good.
 * Expired intents are dropped on read.
 */
function readGeneralChatOpenIntent(
    scope: GeneralConversationPersistenceScope,
): string | null {
    const intentKey = getGeneralOpenIntentKey(scope);
    if (!intentKey) {
        return null;
    }
    const stored = safeReadString(intentKey);
    if (!stored) {
        return null;
    }

    const separatorIndex = stored.indexOf(':');
    const writtenAtMs = Number(stored.slice(0, separatorIndex));
    const conversationId = stored.slice(separatorIndex + 1);
    const isFresh =
        separatorIndex > 0 &&
        Number.isFinite(writtenAtMs) &&
        Date.now() - writtenAtMs < OPEN_INTENT_TTL_MS;
    if (!isFresh || !conversationId) {
        safeWriteString(intentKey, null);
        return null;
    }
    return conversationId;
}

/**
 * Record which conversation this session is on. A null id means the session is
 * on a new chat that has not been created yet, so a reload keeps the draft
 * instead of resuming the user's previous conversation.
 */
export function writeGeneralChatSelection(
    scope: GeneralConversationPersistenceScope,
    conversationId: string | null,
): void {
    const sessionKey = getGeneralSessionChatKey(scope);
    if (!sessionKey || typeof window === 'undefined') {
        return;
    }
    try {
        window.sessionStorage.setItem(
            sessionKey,
            conversationId ?? NEW_CHAT_SESSION_VALUE,
        );
    } catch {
        // sessionStorage unavailable (private mode, partitioned frame)
    }
    const mirrorKey = getGeneralLastConversationKey(scope);
    if (mirrorKey) {
        safeWriteString(mirrorKey, conversationId);
    }
    // The intent is one-shot: once the session owns a selection, drop it so a
    // later reload doesn't reopen it over the user's current chat.
    const intentKey = getGeneralOpenIntentKey(scope);
    if (intentKey) {
        safeWriteString(intentKey, null);
    }
}

function readMirroredGeneralConversationId(
    scope: GeneralConversationPersistenceScope,
): string | null {
    const key = getGeneralLastConversationKey(scope);
    return key ? safeReadString(key) : null;
}

/**
 * Persist panel-open + chat-selection intent before opening a new tab. A
 * fresh tab can't reach this tab's in-memory conversation opener, so it
 * restores the intent from storage on mount. Run-native chats select by run
 * id, so the run id wins when present.
 *
 * This is a deliberate "open THIS conversation", so it outranks the new tab's
 * default of starting a fresh chat. It is consumed once the chat selects it.
 */
export function persistAgentChatOpenIntent(target: {
    readonly agentConversationId: string;
    readonly agentRunId: string | null;
    readonly organizationId: string | null;
    readonly userId: string | null;
}): void {
    const scope = {
        organizationId: target.organizationId,
        userId: target.userId,
    };
    const intentKey = getGeneralOpenIntentKey(scope);
    if (!intentKey) {
        return;
    }

    writePersistedChatVisible(true);
    // Stamped so an intent the opened tab never claims expires, instead of
    // reopening this conversation for every future session.
    safeWriteString(
        intentKey,
        `${Date.now()}:${target.agentRunId ?? target.agentConversationId}`,
    );
}
